import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClientProductView, ProductSummaryView } from '@lp/contracts';
import { useState } from 'react';
import { Link } from 'react-router';
import { del, errorText, get, patch, post, put } from '@/lib/api';
import { useIsSuperAdmin } from '@/lib/auth';
import { date } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { RenameDialog } from './rename-dialog';
import { Badge, Button, Card, ErrorBox, Input, Loading, Select } from './ui';

/** the products a client has: each on its own version; a newer version reaches the client only from here */
export function ClientProducts({ clientId }: { clientId: number }) {
  const t = useT();
  const qc = useQueryClient();
  const superAdmin = useIsSuperAdmin();
  const path = `/platform/clients/${clientId}/products`;
  const mine = useQuery({ queryKey: ['client', clientId, 'products'], queryFn: () => get<ClientProductView[]>(path) });
  const catalog = useQuery({ queryKey: ['products'], queryFn: () => get<ProductSummaryView[]>('/platform/products'), enabled: superAdmin });
  const [pick, setPick] = useState('');
  const [ownEn, setOwnEn] = useState('');
  const [ownKm, setOwnKm] = useState('');
  const [renaming, setRenaming] = useState<ClientProductView | null>(null);
  const done = (list: ClientProductView[]) => {
    qc.setQueryData(['client', clientId, 'products'], list);
    void qc.invalidateQueries({ queryKey: ['products'] });
  };
  const install = useMutation({
    mutationFn: (code: string) => post<ClientProductView[]>(path, { productCode: code, ...(ownEn.trim() ? { nameEn: ownEn.trim() } : {}), ...(ownKm.trim() ? { nameKm: ownKm.trim() } : {}) }),
    onSuccess: (l) => {
      setPick('');
      setOwnEn('');
      setOwnKm('');
      done(l);
    },
  });
  const rename = useMutation({
    mutationFn: (x: { code: string; nameEn: string | null; nameKm: string | null }) => patch<ClientProductView[]>(`${path}/${x.code}`, { nameEn: x.nameEn, nameKm: x.nameKm }),
    onSuccess: (l) => {
      setRenaming(null);
      done(l);
    },
  });
  const move = useMutation({ mutationFn: (x: { code: string; version: number }) => put<ClientProductView[]>(`${path}/${x.code}`, { version: x.version }), onSuccess: done });
  const remove = useMutation({ mutationFn: (code: string) => del<ClientProductView[]>(`${path}/${code}`), onSuccess: done });
  const active = new Set((mine.data ?? []).filter((p) => p.status === 'active').map((p) => p.productCode));
  const installable = (catalog.data ?? []).filter((p) => p.status === 'active' && p.publishedVersion && !active.has(p.code));
  const error = install.error ?? move.error ?? remove.error;

  return (
    <Card title={t('Products')} className="mt-4">
      {mine.isLoading ? (
        <Loading />
      ) : mine.error ? (
        <ErrorBox text={errorText(mine.error)} />
      ) : (
        <>
          {!mine.data?.length ? (
            <p className="mb-3 text-sm text-ink-600">{t('No product installed yet.')}</p>
          ) : (
            <table className="mb-3 w-full text-sm">
              <tbody>
                {mine.data.map((p) => (
                  <tr key={p.productCode} className="border-b border-ink-100 last:border-0" data-testid={`client-product-${p.productCode}`}>
                    <td className="py-2 pr-3">
                      <Link to={`/products/${p.productCode}`} className="font-medium text-ink-900 hover:underline">
                        {p.nameEn}
                      </Link>{' '}
                      <span className="font-mono text-[11px] text-ink-500">{p.productCode}</span>
                      {p.ownName && (
                        <span className="ml-1.5">
                          <Badge tone="blue">{t('Own name')}</Badge>
                        </span>
                      )}
                      <span className="block text-xs text-ink-500">
                        {p.nameKm}
                        {p.ownName && ` · ${t('Platform name')}: ${p.platformNameEn}`}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <Badge>v{p.version}</Badge>
                      {p.newerVersion && p.status === 'active' && (
                        <span className="ml-1.5">
                          <Badge tone="blue">
                            {t('Newer')}: v{p.newerVersion}
                          </Badge>
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">{p.status === 'active' ? <Badge tone="green">{t('Active')}</Badge> : <Badge>{t('Removed')}</Badge>}</td>
                    <td className="py-2 pr-3 text-xs text-ink-500">{date(p.installedAt)}</td>
                    <td className="py-2 text-right">
                      {superAdmin && p.status === 'active' && (
                        <span className="inline-flex gap-1">
                          {p.newerVersion && (
                            <Button variant="secondary" onClick={() => move.mutate({ code: p.productCode, version: p.newerVersion! })} disabled={move.isPending}>
                              {t('Move to')} v{p.newerVersion}
                            </Button>
                          )}
                          <Button variant="ghost" onClick={() => setRenaming(p)}>
                            {t('Rename')}
                          </Button>
                          <Button variant="ghost" onClick={() => remove.mutate(p.productCode)} disabled={remove.isPending}>
                            {t('Remove')}
                          </Button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {superAdmin && installable.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-ink-100 pt-3">
              <Select value={pick} onChange={(e) => setPick(e.target.value)} aria-label={t('Product to install')}>
                <option value="">{t('Product to install')}</option>
                {installable.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.code} — {p.nameEn} (v{p.publishedVersion})
                  </option>
                ))}
              </Select>
              {pick && (
                <>
                  <Input value={ownEn} onChange={(e) => setOwnEn(e.target.value)} maxLength={80} placeholder={t('Own name (English) — optional')} className="w-56" aria-label={t('Own name (English) — optional')} />
                  <Input value={ownKm} onChange={(e) => setOwnKm(e.target.value)} maxLength={80} placeholder={t('Own name (Khmer) — optional')} className="w-56" aria-label={t('Own name (Khmer) — optional')} />
                </>
              )}
              <Button onClick={() => pick && install.mutate(pick)} disabled={!pick || install.isPending}>
                {t('Install')}
              </Button>
            </div>
          )}
          {error && <ErrorBox text={errorText(error)} />}
          {renaming && (
            <RenameDialog
              title={`${t('Name for this client')} — ${renaming.productCode}`}
              nameEn={renaming.nameEn}
              nameKm={renaming.nameKm}
              hint={`${t('Platform name')}: ${renaming.platformNameEn} · ${renaming.platformNameKm}`}
              busy={rename.isPending}
              error={rename.error}
              onSave={(n) => rename.mutate({ code: renaming.productCode, ...n })}
              onReset={renaming.ownName ? () => rename.mutate({ code: renaming.productCode, nameEn: null, nameKm: null }) : undefined}
              onClose={() => setRenaming(null)}
            />
          )}
        </>
      )}
    </Card>
  );
}
